import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import { Chip, IconButton, Slider, Stack } from "@mui/material";
import theme from "@/theme";
import EmojiSvg from "@/assets/svg/EmojiSvg";
import ImageSvg from "@/assets/svg/ImageSvg";
import BearSvg from "@/assets/svg/BearSvg";
import BullSvg from "@/assets/svg/BullSvg";
import { style } from "./stylesCreatePostModal";
import InfoPanel from "./component/InfoPanel";
import PostHeader from "./component/PostHeader";
import { Controller, useForm } from "react-hook-form";
import {
  useCreateLongPostMutation,
  useDeleteAttachmentMutation,
  useUpdatePostMutation,
} from "@/store/api/postApi";
import { useValidateSymbolMutation } from "@/store/api/financeApi";
import { CompositeDecorator, ContentState, EditorState, Modifier } from "draft-js";
import PostEditor from "./component/PostEditor";
import EmojiPicker from "emoji-picker-react";
import { useDispatch, useSelector } from "react-redux";
import { setEditingPostLoading, setPostLoading, setDraftLoading } from "@/store/slice/loadingSlice";
import { setParams } from "@/store/slice/postParamsSlice";
import { RootState } from "@/store/store";
import PostImages from "./component/PostImages";
import PostButton from "./component/CreatePostButton";
import ImagePreviewModal from "../../ImagePreviewModal/ImagePreviewModal";
import { insertMentionToEditor } from "./component/mentions";
import { TICKER_REGEX } from "./component/tickerSchema";

const MentionSpan = (props: any) => <span style={{ color: "#1976d2" }}>{props.children}</span>;

const DollarHashtagSpan = (props: any) => (
  <span
    style={{
      color: theme.palette.custom.mainBlue,
      fontWeight: 500,
      // fontSize: "inherit",
      // lineHeight: "inherit",
    }}
  >
    {props.children}
  </span>
);

const LinkSpan = (props: any) => (
  <a
    href={props.decoratedText}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: theme.palette.custom.mainBlue }}
  >
    {props.children}
  </a>
);

const regexStrategy = (regex: RegExp) => (contentBlock: any, callback: any, _contentState: any) => {
  const text = contentBlock.getText();
  let matchArr, start;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    callback(start, start + matchArr[0].length);
  }
};

const decorator = new CompositeDecorator([
  {
    strategy: regexStrategy(/@\w+/g),
    component: MentionSpan,
  },
  {
    strategy: regexStrategy(TICKER_REGEX),
    component: DollarHashtagSpan,
  },
  {
    strategy: regexStrategy(/https?:\/\/[^\s]+/g),
    component: LinkSpan,
  },
]);

interface CreatePostModalProps {
  open: boolean;
  handleClose: () => void;
  postVariant: string;
  editPost?: any;
  isDraft?: boolean;
  maxImages?: number;
  setToast: (toast: {
    message: string;
    success: boolean;
    link?: { text: string; href: string };
  }) => void;
}

export default function CreatePostModal({
  open,
  handleClose,
  postVariant,
  editPost,
  isDraft = false,
  maxImages = 4,
  setToast,
}: CreatePostModalProps) {
  const [showPicker, setShowPicker] = useState(false);
  const handleEmojiClick = (emojiData: any) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    const newContentState = Modifier.insertText(contentState, selectionState, emojiData.emoji);

    const newEditorState = EditorState.push(editorState, newContentState, "insert-characters");

    setEditorState(newEditorState);
    setShowPicker(false);
  };
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty(decorator));

  const { control, handleSubmit, watch, setValue, getValues } = useForm<any>({
    defaultValues: {
      postText: "",
      selectedChip: [],
      addImage: "",
      sliderValue: 0,
    },
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const subscriptionStatus = useSelector((state: RootState) => state.auth.subscriptionStatus);
  const isPremium = subscriptionStatus === "ACTIVE";
  const maxChars = isPremium ? 25000 : 500;
  const personalMax = isPremium ? 25000 : 300;
  const postText = watch("postText");
  const selectedChip = watch("selectedChip");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [invalidCashtags, setInvalidCashtags] = useState<string[]>([]);
  const [showPublishRequirements, setShowPublishRequirements] = useState(true);
  const [isUrl, setIsUrl] = useState(false);
  const [validateSymbol] = useValidateSymbolMutation();
  const [createLongPost] = useCreateLongPostMutation();
  const [, setRefreshKey] = useState(0);
  const [updatePost] = useUpdatePostMutation();
  const dispatch = useDispatch();
  const params = useSelector((state: RootState) => state.postParams);
  const [deleteAttachment] = useDeleteAttachmentMutation();
  const [deletedAttachments, setDeletedAttachments] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownItems, setDropdownItems] = useState<any[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const insertMention = (mention: { data: { code: string; name?: string } }) => {
    const newState = insertMentionToEditor(editorState, mention.data.code, mention.data.name);
    setEditorState(newState);
    setSearchTerm("");
  };
  const CloseAndSubmit = async () => {
    if (!editPost) {
      await handleSubmit(handleSubmitWithPublished(false))();
    }
    handleClosethisModal();
  };
  const handleSubmitWithPublished = (published: boolean) => async (data: any) => {
    await onSubmit(data, published);
  };

  const handleChipClick = (label: string) => {
    const current = getValues("selectedChip") || [];

    if (postVariant !== "Stocks") {
      if (current.includes(label)) {
        setValue("selectedChip", []);
      } else {
        setValue("selectedChip", [label]);
      }
    } else {
      if (current.includes(label)) {
        setValue(
          "selectedChip",
          current.filter((item: string) => item !== label)
        );
      } else {
        setValue("selectedChip", [...current, label]);
      }
    }
  };
  const togglePublishRequirements = () => {
    setShowPublishRequirements(!showPublishRequirements);
  };

  const getPlaceholder = (variant: string) => {
    switch (variant) {
      case "Stocks":
        return "Share an idea (use $ before symbol e.g $SYMBL)";
      case "Economics":
        return "Include a source for your post, either a link to your source, or relevant image or screenshot, such as a price chart ";
      case "Personal":
        return "Share something...";
    }
  };

  const handleRemoveImage = (index: number) => {
    const removedUrl = imagePreviews[index];

    if (editPost?.attachments) {
      const attachmentToDelete = editPost.attachments.find((att: any) => att.url === removedUrl);
      if (attachmentToDelete?.uid) {
        setDeletedAttachments((prev) => [...prev, attachmentToDelete.uid]);
      }
    }

    if (removedUrl.startsWith("blob:")) {
      const blobPreviews = imagePreviews.filter((url) => url.startsWith("blob:"));
      const blobIndex = blobPreviews.indexOf(removedUrl);
      if (blobIndex !== -1) {
        setImageFiles((prev) => prev.filter((_, i) => i !== blobIndex));
      }
      URL.revokeObjectURL(removedUrl);
    }

    setImagePreviews((prev) => prev.filter((_, i) => i !== index));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCheck = async (tickers: string[]): Promise<string[]> => {
    const invalidTickers: string[] = [];
    await Promise.all(
      tickers.map(async (ticker) => {
        try {
          const res = await validateSymbol({
            symbol: ticker.slice(1).toUpperCase(),
          }).unwrap();
          if (!res.valid) {
            invalidTickers.push(ticker);
          }
        } catch (e) {
          console.error(`API error for ${ticker}:`, e);
          invalidTickers.push(ticker);
        }
      })
    );

    return invalidTickers;
  };

  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const handleSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imageFiles.length >= maxImages) {
      setToast({ message: `You can only upload up to ${maxImages} images.`, success: false });
      return;
    }
    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setToast({ message: "Only JPEG and PNG are allowed.", success: false });
      return;
    }
    setImageFiles((prev) => [...prev, file]);
    setImagePreviews((prev) => [...prev, URL.createObjectURL(file)]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data: any, published: boolean) => {
    const contentState = editorState.getCurrentContent();
    const hasText = contentState.hasText();
    const hasImages = imageFiles.length > 0;
    const plainText = contentState.getPlainText().trim();

    if (!published && !hasText && !hasImages) {
      return;
    }

    const urls = plainText.match(/https?:\/\/[^\s"]+/g) || [];
    const cleanUrls = urls.map((url) => url.replace(/<\/?[^>]+(>|$)/g, ""));

    const formData = new FormData();
    console.log("imageFiles to upload:", imageFiles);
    console.log("imagePreviews:", imagePreviews);
    console.log("deletedAttachments:", deletedAttachments);
    imageFiles.forEach((file) => {
      formData.append("files", file);
    });

    formData.append("content", data.postText);
    formData.append("topic", postVariant.toUpperCase());
    formData.append("isPublished", published.toString());
    formData.append("sentiment", data.sliderValue + 5);
    const formatted = data.selectedChip.map((tag: any) => tag.replace(/^#/, "").toUpperCase());
    formData.append("subtypes", formatted);
    if (cleanUrls) {
      cleanUrls.forEach((url) => {
        formData.append("sources", url);
      });
    }

    handleClose();
    handleClosethisModal();

    const isPublishingDraft =
      published &&
      !!editPost &&
      (isDraft || editPost.published === false || editPost.isPublished === false);

    try {
      if (published) {
        if (isPublishingDraft) {
          dispatch(setDraftLoading({ loading: true, type: "posts" }));
        } else if (editPost) {
          dispatch(
            setEditingPostLoading({
              loading: true,
              postId: editPost.uid,
            })
          );
        } else {
          dispatch(setPostLoading(true));
          dispatch(setParams({ ...params, page: 1 }));
        }
      } else {
        dispatch(
          setDraftLoading({
            loading: true,
            type: "posts",
          })
        );
      }

      if (editPost && deletedAttachments.length > 0) {
        await Promise.all(
          deletedAttachments.map((attachmentUid) =>
            deleteAttachment({ uid: editPost.uid, attachmentUid }).unwrap()
          )
        );
      }

      if (editPost) {
        const updatedPost = await updatePost({ uid: editPost.uid, formData }).unwrap();

        if (isPublishingDraft) {
          const publishedUid = updatedPost?.uid || editPost.uid;
          setToast({
            message: "Post successfully created.",
            success: true,
            link: { text: "View", href: `/post/${publishedUid}` },
          });
        }
      } else {
        const createdPost = await createLongPost(formData).unwrap();
        const newPostUid = createdPost.uid;
        setRefreshKey((prev) => prev + 1);

        if (published) {
          setTimeout(() => {
            setToast({
              message: "Post successfully created.",
              success: true,
              link: { text: "View", href: `/post/${newPostUid}` },
            });
          }, 2000);
        } else {
          setToast({
            message: "Your post has been saved in drafts.",
            success: true,
            link: { text: "View drafts", href: "/drafts" },
          });
        }
      }
    } catch (e: any) {
      setTimeout(() => {
        setToast({
          message: e?.data?.message || "Something went wrong. Please try again.",
          success: false,
        });
      }, 2000);
    } finally {
      if (published) {
        if (isPublishingDraft) {
          dispatch(setDraftLoading({ loading: false, type: null }));
        } else if (editPost) {
          dispatch(
            setEditingPostLoading({
              loading: false,
              postId: null,
            })
          );
        } else {
          dispatch(setPostLoading(false));
        }
      } else {
        dispatch(
          setDraftLoading({
            loading: false,
            type: null,
          })
        );
      }
      setValue("postText", "");
      setEditorState(EditorState.createEmpty(decorator));
    }
  };

  useEffect(() => {
    const contentState = editorState.getCurrentContent();
    const plainText = contentState.getPlainText();
    const urls = plainText.match(/https?:\/\/[^\s"]+/g) || [];
    const cleanUrls = urls.map((url) => url.replace(/<\/?[^>]+(>|$)/g, ""));
    if (cleanUrls.length) {
      setIsUrl(true);
    } else {
      setIsUrl(false);
    }
  });

  useEffect(() => {
    const content = editorState.getCurrentContent();
    const plainText = content.getPlainText();
    const limitedText = plainText.slice(0, maxChars);
    setValue("postText", limitedText);
  }, [editorState]);

  useEffect(() => {
    if (editPost) {
      const contentState = ContentState.createFromText(editPost.content);
      setEditorState(EditorState.createWithContent(contentState, decorator));

      const subtype =
        Array.isArray(editPost.subtypes) && editPost.subtypes.length > 0
          ? `#${editPost.subtypes[0].toLowerCase()}`
          : "";
      setValue("selectedChip", subtype ? [subtype] : []);
      setValue("postText", editPost.content);
      setValue("sliderValue", editPost.sentiment - 5);
      if (editPost.attachments?.length) {
        const urls = editPost.attachments.map((att: any) => att.url);
        setImagePreviews(urls);
      }
    }
  }, [editPost]);
  useEffect(() => {
    if (editPost) return;
    if (postVariant === "Stocks") setValue("selectedChip", [""]);
    else if (postVariant === "Economics") setValue("selectedChip", ["#economics"]);
    else if (postVariant === "Personal") setValue("selectedChip", ["#personal"]);
  }, [postVariant, editPost]);

  const handleCloseModal = () => {
    handleClosethisModal();
  };
  const handleClosethisModal = () => {
    handleClose();
    setValue("selectedChip", []);
    setInvalidCashtags([]);
    setImageFiles([]);
    setImagePreviews([]);
    setDeletedAttachments([]);
    setEditorState(EditorState.createEmpty(decorator));
    setValue("sliderValue", 0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const plainText = editorState.getCurrentContent().getPlainText();
    const cashtags = plainText.match(TICKER_REGEX) || [];

    if (cashtags.length === 0) {
      setInvalidCashtags([]);
      return;
    }
    const handler = setTimeout(async () => {
      const invalids = await handleCheck(cashtags);
      const currentText = editorState.getCurrentContent().getPlainText();
      const currentCashtags = currentText.match(/\$[A-Za-z0-9.-]+/g) || [];
      if (JSON.stringify(currentCashtags) === JSON.stringify(cashtags)) {
        setInvalidCashtags(invalids);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [editorState, postVariant]);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;

      const items = e.clipboardData.items;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            const allowedTypes = ["image/jpeg", "image/png"];
            if (!allowedTypes.includes(file.type)) {
              setToast({ message: "Only JPEG and PNG are allowed.", success: false });
              return;
            }
            setImageFiles((prev) => [...prev, file]);
            setImagePreviews((prev) => [...prev, URL.createObjectURL(file)]);
          }
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, []);

  const remainingChars =
    postVariant === "Personal"
      ? personalMax - postText.length
      : maxChars - postText.replace(/https?:\/\/[^\s]+/, "").length;

  const charCounterText =
    remainingChars >= 0
      ? remainingChars
      : `Content must be shorter than or equal to ${postVariant === "Personal" ? personalMax : maxChars} characters`;

  const variantChips: Record<string, string[]> = {
    Stocks: ["#fundamental", "#technical"],
    Economics: ["#economics"],
    Personal: ["#personal"],
  };

  useEffect(() => {
    setDropdownItems(dropdownItems);
  }, [dropdownItems, setDropdownItems]);

  const isStocksValid = useMemo(() => {
    if (postVariant !== "Stocks") return true;

    const plainText = editorState.getCurrentContent().getPlainText();
    const cashtags = plainText.match(/\$[A-Za-z0-9.-]+/g) || [];
    const hasValidCashtag = cashtags.length > 0 && invalidCashtags.length === 0;
    const hasSubtype = selectedChip.some((chip: string) => chip && chip !== "");

    return hasValidCashtag && hasSubtype;
  }, [postVariant, editorState, invalidCashtags, selectedChip]);

  const isEconomicsValid = useMemo(() => {
    if (postVariant !== "Economics") return true;

    const hasSource = isUrl || imagePreviews.length > 0;
    const hasSubtype = selectedChip.includes("#economics");

    return hasSource && hasSubtype;
  }, [postVariant, isUrl, imagePreviews, selectedChip]);

  const isFormValid = useMemo(() => {
    const hasContent = postText.trim().length > 0 || imagePreviews.length > 0;
    const isWithinCharLimit = remainingChars >= 0;
    const hasNoInvalidCashtags = invalidCashtags.length === 0;

    return (
      hasContent && isWithinCharLimit && hasNoInvalidCashtags && isStocksValid && isEconomicsValid
    );
  }, [postText, imagePreviews, remainingChars, invalidCashtags, isStocksValid, isEconomicsValid]);

  const validationState = useMemo(() => {
    const plainText = editorState?.getCurrentContent()?.getPlainText() || "";
    const cashtags = plainText.match(/\$[A-Za-z0-9.-]+/g) || [];

    return {
      hasValidCashtag:
        postVariant === "Stocks" ? cashtags.length > 0 && invalidCashtags.length === 0 : true,
      hasSubtype:
        postVariant === "Stocks"
          ? selectedChip.some((chip: string) => chip && chip !== "")
          : postVariant === "Economics"
            ? selectedChip.includes("#economics")
            : selectedChip.includes("#personal"),
      hasSource: isUrl || imagePreviews.length > 0,
      isButtonActive: isFormValid,
    };
  }, [postVariant, editorState, invalidCashtags, selectedChip, isUrl, imagePreviews, isFormValid]);

  return (
    <div style={{ position: "relative", display: postVariant ? "block" : "none" }}>
      <Modal
        open={!!open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        BackdropProps={{
          sx: {
            backgroundColor: "#ffffff90",
          },
        }}
      >
        <Box
          sx={{ ...style.modalWrapper, paddingBottom: showPublishRequirements ? "180px" : "40px" }}
          onClick={CloseAndSubmit}
        >
          <Box sx={style.modal} onClick={(e) => e.stopPropagation()}>
            <PostHeader handleClose={handleClosethisModal} invalidCashtags={invalidCashtags} />
            <form onSubmit={handleSubmit(handleSubmitWithPublished(true))}>
              <Box sx={style.modalContent}>
                <Controller
                  name="postText"
                  control={control}
                  render={() => (
                    <PostEditor
                      editorState={editorState}
                      setEditorState={setEditorState}
                      placeholder={getPlaceholder(postVariant)}
                      maxChars={postVariant === "Personal" ? personalMax : maxChars}
                      setSearchTerm={setSearchTerm}
                      setDropdownItems={setDropdownItems}
                      setHighlightedIndex={setHighlightedIndex}
                      onSelectMention={insertMention}
                      dropdownItems={dropdownItems}
                      searchTerm={searchTerm}
                      highlightedIndex={highlightedIndex}
                      postVariant={postVariant}
                    />
                  )}
                />

                {imagePreviews.length > 0 && (
                  <PostImages
                    images={imagePreviews}
                    onRemoveImage={handleRemoveImage}
                    onImageClick={(index) => {
                      setSelectedIndex(index);
                      setModalOpen(true);
                    }}
                  />
                )}

                <ImagePreviewModal
                  images={imagePreviews}
                  open={modalOpen}
                  onClose={() => setModalOpen(false)}
                  initialIndex={selectedIndex}
                />

                <Box sx={{ display: "flex", gap: 1, alignItems: "center", marginTop: "14px" }}>
                  {variantChips[postVariant]?.map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      clickable
                      onClick={() => handleChipClick(chip)}
                      sx={style.chipStyle(selectedChip, chip)}
                    />
                  ))}
                  <Box sx={{ flexGrow: 1, textAlign: "right" }}>
                    <Typography
                      sx={{
                        ...style.charCounter,
                        fontWeight: remainingChars < 0 ? 700 : 400,
                      }}
                      color={remainingChars >= 0 ? "text.secondary" : "error"}
                    >
                      {charCounterText}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  height: "54px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {
                  <Stack position="relative" direction="row" spacing={1}>
                    <IconButton size="small" color="primary">
                      <EmojiSvg onClick={() => setShowPicker(!showPicker)} cursor={"pointer"} />
                    </IconButton>
                    {showPicker && (
                      <div
                        style={{ position: "absolute", zIndex: 10, left: "-20px", bottom: "65px" }}
                      >
                        <EmojiPicker onEmojiClick={handleEmojiClick} />
                      </div>
                    )}
                    <label htmlFor="upload-image">
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="upload-image"
                        accept="image/jpeg,image/png"
                        hidden
                        onChange={(e) => handleSelectImage(e)}
                      />
                      {imageFiles.length < maxImages && (
                        <IconButton size="small" color="primary" component="span">
                          <ImageSvg />
                        </IconButton>
                      )}
                    </label>
                  </Stack>
                }

                {postVariant === "Stocks" && (
                  <Stack direction="row" alignItems="center">
                    <Box sx={style.sliderInfo}>
                      <BearSvg width={42} height={23} big />
                      <Typography
                        color={theme.palette.custom.blueGray900}
                        fontWeight={500}
                        variant="body1"
                      >
                        Strong sell
                      </Typography>
                    </Box>

                    <Box position="relative">
                      <Controller
                        name="sliderValue"
                        control={control}
                        render={({ field }) => (
                          <Slider
                            {...field}
                            sx={style.sliderMiddleSide}
                            valueLabelDisplay="auto"
                            min={-5}
                            max={5}
                          />
                        )}
                      />
                      <Typography
                        sx={{
                          position: "absolute",
                          bottom: -10,
                          left: "50%",
                          transform: "translateX(-50%)",
                        }}
                      >
                        Neutral
                      </Typography>
                    </Box>

                    <Box sx={style.sliderInfo}>
                      <BullSvg width={42} height={23} />
                      <Typography
                        color={theme.palette.custom.blueGray900}
                        fontWeight={500}
                        variant="body1"
                      >
                        Strong buy
                      </Typography>
                    </Box>
                  </Stack>
                )}

                <PostButton
                  postVariant={postVariant}
                  invalidCashtags={invalidCashtags}
                  selectedChip={selectedChip}
                  postText={postText}
                  isUrl={isUrl}
                  imagePreviews={imagePreviews}
                />
              </Box>
            </form>
            {postVariant?.length && postVariant !== "Personal" && (
              <InfoPanel
                showPublishRequirements={showPublishRequirements}
                togglePublishRequirements={togglePublishRequirements}
                postVariant={postVariant}
                validationState={validationState}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
